import React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import WalletBetrBucksIcon from '@/assets/icons/wallet-betr-bucks';
import { EntryCardPromoTag } from '@/components/entry-card';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { isNoSweatPromo, isProfitBoostPromo } from '@/feature/bets-sbk/utils/bet-promo-utils';
import { useFormatCurrency } from '@/hooks/use-format-currency';

const PROMO_BONUS_ICON = <WalletBetrBucksIcon size={16} />;

/**
 * Promo badge for a sportsbook bet card.
 * - No Sweat leads with its refund coverage and has no bonus segment - the refund surfaces in the
 *   payout slot once rewarded.
 * - Play and Get is a separate reward for placing the bet, not part of its payout, so it shows only
 *   the applied tag.
 */
export const useSbkEntryPromos = (bet: Bet, isSettled: boolean): ReactNode[] | undefined => {
    const { t } = useTranslation(['bets', 'common']);
    const { formatCurrency } = useFormatCurrency();

    const promotion = bet.promotion;
    if (!promotion) {
        return undefined;
    }

    if (isNoSweatPromo(promotion)) {
        const label = promotion.potential_bonus_amount
            ? t('no_sweat_applied', { amount: formatCurrency(promotion.potential_bonus_amount) })
            : t('common:promo_applied', { name: promotion.title });
        return [<EntryCardPromoTag key={promotion.campaign_token} label={label} />];
    }

    const bonusAmount = isSettled ? promotion.bonus_amount : promotion.potential_bonus_amount;
    const overflow =
        bonusAmount && isProfitBoostPromo(promotion)
            ? { label: t('promo_bonus_amount', { amount: formatCurrency(bonusAmount) }), icon: PROMO_BONUS_ICON }
            : undefined;

    return [
        <EntryCardPromoTag
            key={promotion.campaign_token}
            label={t('common:promo_applied', { name: promotion.title })}
            overflow={overflow}
        />,
    ];
};
